import { useEffect, useMemo, useState } from "react";
import { Option } from "../../common/commonSelect";
import webservice_url, {
  master_baseUrl,
} from "../../webservice/Webservice_url";
import useApi from "../useApi";

interface DataTypes {
  project_id: string;
  project_code: string;
  project_name: string;
}

// ========= Admin PORTAL ===========

// Projects by multiple organization IDs
// Projects by multiple organization IDs
export function useProjectsByOrgId(orgIds: string[]) {
  const [dataList, setDataList] = useState<DataTypes[]>([]);

  // API
  const { callApi, loading, error } = useApi<any>("", {
    method: "POST",
    autoCall: false,
    handleGlobalError: false,
  });

  // Convert API data into dropdown options
  const options = useMemo<Option[]>(() => {
    if (!dataList?.length) {
      return [];
    }

    return dataList.map((d) => ({
      label: d?.project_name ?? "",
      value: d?.project_id ?? "",
    }));
  }, [dataList]);

  // Fetch projects for selected organizations
  const fetchData = async () => {
    // No organization selected
    if (!Array.isArray(orgIds) || orgIds.length === 0) {
      setDataList([]);
      return;
    }

    try {
      const payload = {
        orgIds: orgIds,
      };

      const res = await callApi(payload, webservice_url.get_accounts);

      const mergedData: DataTypes[] = Array.isArray(res?.data) ? res.data : [];

      const uniqueData: DataTypes[] = Array.from(
        new Map<string, DataTypes>(
          mergedData.map((item) => [item.project_id, item]),
        ).values(),
      );

      setDataList(uniqueData);
    } catch (err: any) {
      console.error("Error fetching projects:", err);
      setDataList([]);
    }
  };

  // Fetch whenever selected organizations change
  useEffect(() => {
    if (!Array.isArray(orgIds) || orgIds.length === 0) {
      setDataList([]);
      return;
    }

    fetchData();

    // fetchData intentionally omitted
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [JSON.stringify(orgIds)]);

  return {
    dataList,
    options,
    loading,
    error,
    fetchData,
  };
}

// Projects by managerId
export function useProjectsByManagerId(managerId: string, orgId: string) {
  const [dataList, setDataList] = useState<DataTypes[]>([]);

  // API
  const { callApi, loading } = useApi<any>(
    webservice_url.get_accounts_by_managerId,
    {
      method: "POST",
      autoCall: false,
      handleGlobalError: false,
    },
  );

  const options = useMemo<Option[]>(() => {
    if (!dataList?.length) {
      return [];
    }

    return dataList.map((d) => ({
      label: d?.project_name ?? "",
      value: d?.project_id ?? "",
    }));
  }, [dataList]);

  // Fetch data
  const fetchData = async () => {
    try {
      const res = await callApi({
        manager_id: managerId ?? "",
        org_id: orgId ?? "",
      });

      setDataList(Array.isArray(res) ? res : (res?.data ?? []));
    } catch (err: any) {
      console.error(err);
      setDataList([]);
    }
  };

  useEffect(() => {
    if (!managerId || !orgId) {
      return;
    }

    fetchData();
  }, [managerId, orgId]);

  return {
    dataList,
    options,
    loading,
  };
}

// ========= END ADMIN PORTAL ===========

// ========= VERIFIER PORTAL ===========

export function useProjectsByOrgId_Verifier(orgId: string) {
  const [dataList, setDataList] = useState<DataTypes[]>([]);

  const { callApi, loading } = useApi<any>("", {
    method: "GET",
    autoCall: false,
    handleGlobalError: false,
  });

  const options = useMemo<Option[]>(() => {
    return dataList.map((d) => ({
      label: d.project_name,
      value: d.project_id,
    }));
  }, [dataList]);

  const fetchData = async () => {
    if (!orgId) {
      setDataList([]);
      return;
    }

    try {
      const res = await callApi(
        undefined,
        `${webservice_url.fetchAccounts}?org_id=${orgId}`,
      );

      setDataList(Array.isArray(res) ? res : (res?.data ?? []));
    } catch (err) {
      setDataList([]);
      console.error(err);
    }
  };

  return {
    dataList,
    options,
    loading,
    fetchData,
  };
}

// ========= END VERIFIER PORTAL ===========

// Projects by multiple organization ID
export function useProjectsByMultiOrgId(orgId: string) {
  const [dataList, setDataList] = useState<DataTypes[]>([]);

  // API
  const { callApi, loading, error } = useApi<any>("", {
    method: "GET",
    autoCall: false,
    handleGlobalError: false,
  });

  const options = useMemo<Option[]>(() => {
    if (!dataList?.length) {
      return [];
    }

    return dataList.map((d) => ({
      label: d?.project_name ?? "",
      value: d?.project_id ?? "",
    }));
  }, [dataList]);

  // Fetch data
  const fetchData = async () => {
    try {
      const res = await callApi(
        undefined,
        `${master_baseUrl}/organizations/${orgId}/projects`,
      );

      setDataList(Array.isArray(res) ? res : (res?.data ?? []));
    } catch (err: any) {
      setDataList([]);
      console.error(err);
    }
  };

  useEffect(() => {
    if (!orgId) {
      setDataList([]);
      return;
    }

    fetchData();

    // fetchData intentionally omitted
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [orgId]);

  return {
    dataList,
    options,
    loading,
    error,
  };
}
